/**
 * resolveEffectiveSchedule — Pure utility for flexible training scheduling.
 *
 * Resolves the effective day for each program block by overlaying
 * user schedule mappings on top of the original AI-generated weekly_plan.
 *
 * Resolution rule:
 *   effective_day = schedule_map[block_index].assigned_day ?? weekly_plan[block_index].day
 *
 * This module is STATELESS and does NOT fetch data.
 * Consumers provide both the original plan and any existing mappings.
 *
 * @module resolveEffectiveSchedule
 * @since Step 1 — Additive scheduling foundation
 */

// ── Types ────────────────────────────────────────────────────────

/** A single day entry from content_json.simple.weekly_plan */
export interface OriginalDayPlan {
  day: string;            // "Lundi", "Mardi", etc.
  focus?: string;
  session_type?: string;
  duration_hint_min?: number;
  intensity_hint?: string;
  [key: string]: unknown; // preserve extra fields
}

/** A schedule mapping row from program_schedule_map */
export interface ScheduleMapEntry {
  block_index: number;
  original_day: string;
  assigned_day: string;
  assigned_time?: string | null;
  reason?: string | null;
  ai_warnings?: unknown[];
  source?: string;
}

/** Resolved block with effective day determined */
export interface ResolvedDayPlan extends OriginalDayPlan {
  /** The effective day after applying schedule map (or original if no mapping) */
  effective_day: string;
  /** Index in the original weekly_plan array */
  block_index: number;
  /** Whether this block has been remapped by the user */
  is_remapped: boolean;
  /** The assigned time if set */
  assigned_time?: string | null;
  /** Reason for remapping */
  remap_reason?: string | null;
  /** Cached AI warnings for this remapping */
  ai_warnings?: unknown[];
}

// ── Core resolver ────────────────────────────────────────────────

/**
 * Resolve effective schedule by overlaying mappings on the original plan.
 *
 * @param weeklyPlan - Original weekly_plan array from content_json.simple
 * @param mappings   - Schedule map entries from program_schedule_map (can be empty)
 * @returns Resolved plan with effective days, preserving original order
 */
export function resolveEffectiveSchedule(
  weeklyPlan: OriginalDayPlan[],
  mappings: ScheduleMapEntry[] = []
): ResolvedDayPlan[] {
  // Index mappings by block_index for O(1) lookup
  const mapByIndex = new Map<number, ScheduleMapEntry>();
  for (const m of mappings) {
    mapByIndex.set(m.block_index, m);
  }

  return weeklyPlan.map((block, index) => {
    const mapping = mapByIndex.get(index);

    if (mapping) {
      return {
        ...block,
        effective_day: mapping.assigned_day,
        block_index: index,
        is_remapped: true,
        assigned_time: mapping.assigned_time ?? null,
        remap_reason: mapping.reason ?? null,
        ai_warnings: mapping.ai_warnings ?? [],
      };
    }

    return {
      ...block,
      effective_day: block.day,
      block_index: index,
      is_remapped: false,
      assigned_time: null,
      remap_reason: null,
      ai_warnings: [],
    };
  });
}

// ── Helpers ──────────────────────────────────────────────────────

/**
 * Compute the flat block_index offset for a given week within a multi-week plan.
 *
 * Each week's days are indexed sequentially across the full plan:
 *   week 0 → offset 0
 *   week 1 → offset = week0.days.length
 *   week 2 → offset = week0.days.length + week1.days.length
 *   etc.
 *
 * @param weeks   - The full weekly_plan array
 * @param weekIdx - The 0-based index of the target week
 * @returns The flat offset for the first day of that week
 */
export function computeBlockOffset(
  weeks: { days?: unknown[] }[],
  weekIdx: number
): number {
  let offset = 0;
  for (let i = 0; i < weekIdx && i < weeks.length; i++) {
    offset += (weeks[i].days?.length || 0);
  }
  return offset;
}

/**
 * Check if any block in the program has been remapped.
 */
export function hasScheduleOverrides(mappings: ScheduleMapEntry[]): boolean {
  return mappings.length > 0;
}

/**
 * Get the effective day for a single block index.
 * Useful for point lookups without resolving the full schedule.
 */
export function getEffectiveDay(
  blockIndex: number,
  originalDay: string,
  mappings: ScheduleMapEntry[]
): string {
  const mapping = mappings.find((m) => m.block_index === blockIndex);
  return mapping?.assigned_day ?? originalDay;
}

/**
 * Reverse-lookup: given an effective (displayed) day name, find the original
 * content_json day name for the corresponding block.
 *
 * This is needed when the user interacts with an effective day (e.g. assigns
 * a session to "Mercredi" which is the remapped day) but the mutation must
 * target the original content_json entry (e.g. "Lundi").
 *
 * If no mapping matches, returns effectiveDay unchanged (no remap exists).
 */
export function getOriginalDay(
  effectiveDay: string,
  weekDays: { day: string }[],
  weekBlockOffset: number,
  mappings: ScheduleMapEntry[]
): string {
  if (!mappings.length) return effectiveDay;

  for (let i = 0; i < weekDays.length; i++) {
    const blockIdx = weekBlockOffset + i;
    const mapping = mappings.find((m) => m.block_index === blockIdx);
    const eff = mapping?.assigned_day ?? weekDays[i].day;
    if (eff === effectiveDay) {
      return weekDays[i].day;
    }
  }
  return effectiveDay;
}

/** Dual-name day entry for pickers that need both effective and original names */
export interface DualDayEntry {
  weekNumber: number;
  /** The day name the user sees (effective / remapped) */
  dayName: string;
  /** The original day name in content_json (for mutations) */
  originalDayName: string;
}
